import React, { useState } from 'react';

import moment, { Moment } from 'moment';
import { useSelector } from 'react-redux';

import { userSelector } from '../../reducers/current-user-selectors';

import { ApplicantExport } from '../../reporting/applicant-export';
import { UiButton } from '../../ui/ui-button';
import { DatePickerError, UiDatePicker } from '../../ui/ui-date-picker';
import { UiDialog } from '../../ui/ui-dialog';
import { UiSelect } from '../../ui/ui-select';

export const ExportApplicantsDialog = ({
  orgs,
  orgId,
  onClose,
  jobId,
  statusFilter,
  nameOrPhoneNumberFilter,
  locationFilter,
  includeRejectedExpired,
}: {
  orgs: Org[];
  orgId?: number;
  onClose: () => void;
  jobId: number | null;
  statusFilter?: string;
  nameOrPhoneNumberFilter?: string;
  locationFilter?: string;
  includeRejectedExpired?: boolean;
}) => {
  const TODAY = moment();

  const [selectedOrgId, setSelectedOrgId] = useState<string>(orgId ? orgId.toString() : '');
  const [startDate, setStartDate] = useState<Moment | null>(moment(TODAY).subtract(120, 'd'));
  const [endDate, setEndDate] = useState<Moment | null>(moment(TODAY));
  const [startError, setStartError] = useState<DatePickerError | null>(null);
  const [endError, setEndError] = useState<DatePickerError | null>(null);
  const [exportError, setExportError] = useState<string>('');

  const user = useSelector(userSelector);
  const orgOptions = orgs.map((o) => ({ label: o.name, value: o.org_id.toString() }));
  const org = orgs.find((o) => o.org_id.toString() === selectedOrgId);

  const showEeoExport =
    (org?.usesEqualOpportunityQuestions || org?.usesFederalContractorQuestions) &&
    (user.isAccountAdmin || user.isAdmin);

  const handleStartChange = (date: Moment | null) => {
    setStartDate(date);
  };

  const handleEndChange = (date: Moment | null) => {
    setEndDate(date);
  };

  const handleStartError = (e: DatePickerError | null) => {
    setStartError(e);
  };

  const handleEndError = (e: DatePickerError | null) => {
    setEndError(e);
  };

  const handleOrgSelect = (id: string) => {
    setSelectedOrgId(id);
  };

  return (
    <UiDialog onClose={onClose} title="Export Applicants" size="sm">
      <UiDialog.Content>
        <div className="u-flexColumn u-alignItems-center">
          {!orgId && (
            <>
              <div className="typo-title">
                Please select an organization to export applicants from
              </div>
              <div className="u-width-80 u-margin-0Auto u-margin-top-lg">
                <UiSelect
                  value={selectedOrgId}
                  options={orgOptions}
                  label="Organization"
                  onChange={handleOrgSelect}
                  fullWidth
                />
              </div>
            </>
          )}
          {selectedOrgId && (
            <>
              <div className="typo-title u-margin-top-lg">
                Please select date range to export applicants
              </div>
              <div className="u-flex-center u-margin-top-lg">
                <UiDatePicker
                  className="u-margin-right-lg"
                  label="Include Applicants From"
                  onChange={handleStartChange}
                  defaultDate={startDate}
                  required
                  disableFuture={!endDate?.isBefore(moment())}
                  maxDate={endDate}
                  maxDateMessage="Cannot be after end date"
                  onError={handleStartError}
                  bottomPadding
                />
                <UiDatePicker
                  label="Include Applicants To"
                  onChange={handleEndChange}
                  defaultDate={endDate}
                  required
                  disableFuture
                  minDate={startDate}
                  minDateMessage="Must be before start date"
                  onError={handleEndError}
                  bottomPadding
                />
              </div>
            </>
          )}
        </div>
      </UiDialog.Content>
      <UiDialog.Actions>
        <div className="u-flexColumn u-width-80 u-margin-0Auto">
          {selectedOrgId && (
            <>
              <div className="u-flexColumn u-margin-bottom">
                {exportError && (
                  <div className="u-color-red typo-bold u-textAlign-center u-margin-bottom-xl">
                    {exportError}
                  </div>
                )}
                <ApplicantExport
                  orgId={parseInt(selectedOrgId, 10)}
                  jobId={jobId}
                  startDate={startDate}
                  endDate={endDate}
                  startError={startError}
                  endError={endError}
                  filter={statusFilter}
                  chartType="source"
                  includeAutoScreenedApplicants
                  disabled={!startDate || !endDate}
                  dataType="reporting"
                  onClose={onClose}
                  setExportError={setExportError}
                  additionalFilters={{
                    nameOrPhoneNumberFilter,
                    locationFilter,
                    includeRejectedExpired: includeRejectedExpired ? 1 : 0,
                  }}
                />
              </div>
              {showEeoExport && (
                <div className="u-flexColumn u-margin-bottom">
                  <ApplicantExport
                    orgId={parseInt(selectedOrgId, 10)}
                    jobId={jobId}
                    startDate={startDate}
                    endDate={endDate}
                    startError={startError}
                    endError={endError}
                    filter={statusFilter}
                    chartType="source"
                    includeAutoScreenedApplicants
                    disabled={!startDate || !endDate || !!nameOrPhoneNumberFilter}
                    dataType="eeo"
                    onClose={onClose}
                    setExportError={setExportError}
                    additionalFilters={{
                      nameOrPhoneNumberFilter,
                      locationFilter,
                      includeRejectedExpired: includeRejectedExpired ? 1 : 0,
                    }}
                  />
                  {nameOrPhoneNumberFilter && (
                    <div className="typo-subtitle u-textAlign-center u-margin-top">
                      To export EEO information, please clear the name search field
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <UiButton onClick={onClose} variant="secondary" size="sm">
            Cancel
          </UiButton>
        </div>
      </UiDialog.Actions>
    </UiDialog>
  );
};
